import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type {
  DoctorWorkingHour,
  WorkingHoursUpdateStatus,
} from '@/types/settings-api';
import { doctorWorkingHoursListQueryKey } from './use-doctor-working-hours-list';
import { doctorWorkingHourByIdQueryKey } from './use-doctor-working-hour-by-id';

/**
 * Update an existing doctor working-hours profile (PROD-762 PR-D).
 *   PUT /api/Doctor/WorkingHour/{workingHourId}
 *
 * Note the SINGULAR `WorkingHour` in the URL path — distinct from the
 * PLURAL `WorkingHours` in the GET-by-id path (the singular/plural
 * discrimination is load-bearing — see
 * `use-doctor-working-hour-by-id.ts` for the GET-side quirk).
 *
 * Flutter parity: `updateDoctorWorkingHours` in
 * `settings_data_source_impl.dart:1122`. Body shape verified against
 * working dev cURL 2026-05-19.
 *
 * Wire-shape notes (verified against cURL — do NOT regress):
 *   - `workingHourId` is the existing GUID and ALSO drives the URL path.
 *   - `practiceName` ships as the real practice display name (e.g.
 *     `"Marina Dubai Office"`), NOT `""`.
 *   - `organizationId` ships as a GUID; `organizationTimezone` ships
 *     as the IANA tz string (e.g. `"Asia/Dubai"`).
 *   - Existing per-slot `id` values are UPPERCASE GUIDs in the cURL —
 *     preserve them verbatim from the GET response. Newly-added slots
 *     keep `""` so the backend allocates.
 *   - Same human-format hour / minute encoding as create.
 *   - `locationIds: null` even when `locations[]` is populated.
 *
 * Invalidates BOTH the list cache (so the Schedule tab refreshes) AND
 * the by-id slot (so a re-open of the editor sees fresh data).
 */
export function useUpdateDoctorWorkingHours(doctorSetupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DoctorWorkingHour) => {
      if (!body.workingHourId) {
        throw new Error(
          'useUpdateDoctorWorkingHours requires a non-empty workingHourId',
        );
      }
      // Returns an UpdateSlotStatus envelope (HTTP 200 even when the change
      // is REJECTED by booked appointments) — caller inspects `blockers[]`
      // (PROD-1825).
      return put<WorkingHoursUpdateStatus>(
        `/api/Doctor/WorkingHour/${body.workingHourId}`,
        body,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: doctorWorkingHoursListQueryKey(doctorSetupId),
      });
      queryClient.invalidateQueries({
        queryKey: doctorWorkingHourByIdQueryKey(variables.workingHourId),
      });
    },
  });
}
